import React, { useState, useEffect, useRef, useCallback } from 'react';
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from 'react-redux';
import moment from 'moment';
import './CombinedDateTimePicker.css';

const CombinedDateTimePicker = ({
  label,
  value,
  onChange,
  placeholder = "Select date and time",
  name,
  disabled = false,
  ampm = true,
  isValidDate = null,
  minDate = null,
  maxDate = null,
}) => {
  const appSettings = useSelector((state) => state.appSettings);
  const { appTheme } = appSettings;
  const isDarkMode = useDarkMode();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(value ? moment(value) : null);
  const [currentMonth, setCurrentMonth] = useState(moment());
  const [currentHour, setCurrentHour] = useState(selectedDate ? selectedDate.hour() : moment().hour());
  const [currentMinute, setCurrentMinute] = useState(selectedDate ? selectedDate.minute() : moment().minute());
  const [isPM, setIsPM] = useState(selectedDate ? selectedDate.hour() >= 12 : moment().hour() >= 12);
  const containerRef = useRef(null);

  const themeColor = useThemeColor();

  

  useEffect(() => {
    if (value && moment(value).isValid()) {
      const momentValue = moment(value);
      setSelectedDate(momentValue);
      setCurrentMonth(momentValue.clone().startOf('month'));
      setCurrentHour(momentValue.hour());
      setCurrentMinute(momentValue.minute());
      setIsPM(momentValue.hour() >= 12);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const toggleDateTimePicker = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  };

  const handleDateSelect = (date) => {
    const newDateTime = moment(date)
      .hour(currentHour)
      .minute(currentMinute)
      .second(0);
    setSelectedDate(newDateTime);
    updateDateTime(newDateTime);
  };

  const handleTimeChange = (hour, minute) => {
    setCurrentHour(hour);
    setCurrentMinute(minute);
    if (selectedDate) {
      const newDateTime = selectedDate.clone()
        .hour(hour)
        .minute(minute)
        .second(0);
      setSelectedDate(newDateTime);
      updateDateTime(newDateTime);
    }
  };

  const updateDateTime = (dateTime) => {
    if (onChange) {
      onChange(dateTime);
    }
  };

  const handleClear = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedDate(null);
    if (onChange) {
      onChange(null);
    }
    setIsOpen(false);
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => prev.clone().add(direction, 'month'));
  };

  const renderCalendar = () => {
    const startOfMonth = currentMonth.clone().startOf('month');
    const endOfMonth = currentMonth.clone().endOf('month');
    const startOfCalendar = startOfMonth.clone().startOf('week');
    const endOfCalendar = endOfMonth.clone().endOf('week');

    const days = [];
    let current = startOfCalendar.clone();

    while (current.isSameOrBefore(endOfCalendar)) {
      const date = current.clone();
      const isCurrentMonth = date.month() === currentMonth.month();
      const isSelected = selectedDate && date.isSame(selectedDate, 'day');
      const isToday = date.isSame(moment(), 'day');
      // Default behavior: disable past dates if no custom validation is provided
      const defaultDisabled = !isValidDate && !minDate && !maxDate ? date.isBefore(moment().startOf('day')) : false;
      
      const isDisabled = defaultDisabled ||
                        (isValidDate && !isValidDate(date)) ||
                        (minDate && date.isBefore(minDate, 'day')) ||
                        (maxDate && date.isAfter(maxDate, 'day'));

      days.push(
        <button
          key={date.format('YYYY-MM-DD')}
          className={`combined-calendar-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${!isCurrentMonth ? 'other-month' : ''}`}
          onClick={() => !isDisabled && handleDateSelect(date)}
          disabled={isDisabled}
          style={{
            backgroundColor: isSelected ? themeColor : 'transparent',
            color: isSelected ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
            opacity: !isCurrentMonth ? 0.3 : (isDisabled ? 0.5 : 1),
            border: isToday && !isSelected ? `1px solid ${themeColor}` : '1px solid transparent'
          }}
        >
          {date.date()}
        </button>
      );
      current.add(1, 'day');
    }

    return days;
  };

  const renderTimeSelection = () => {
    const hours = [];
    const minutes = [];
    const now = moment();
    const isToday = selectedDate && selectedDate.isSame(now, 'day');

    // Generate hours
    for (let h = (ampm ? 1 : 0); h <= (ampm ? 12 : 23); h++) {
      const actualHour = ampm ? (isPM ? (h === 12 ? 12 : h + 12) : (h === 12 ? 0 : h)) : h;
      const displayHour = ampm ? h : h;
      const isSelected = currentHour === actualHour;
      
      // Disable past hours if today is selected
      const isPastHour = isToday && actualHour < now.hour();
      
      hours.push(
        <button
          key={`hour-${h}`}
          className={`combined-time-unit ${isSelected ? 'selected' : ''}`}
          onClick={() => !isPastHour && handleTimeChange(actualHour, currentMinute)}
          disabled={isPastHour}
          style={{
            backgroundColor: isSelected ? themeColor : 'transparent',
            color: isSelected ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
            opacity: isPastHour ? 0.5 : 1,
            cursor: isPastHour ? 'not-allowed' : 'pointer',
          }}
        >
          {displayHour.toString().padStart(2, '0')}
        </button>
      );
    }

    // Generate minutes
    for (let m = 0; m < 60; m += 5) {
      const isSelected = currentMinute === m;
      
      // Disable past minutes if today is selected and current hour is selected
      const isPastMinute = isToday && currentHour === now.hour() && m < now.minute();
      
      minutes.push(
        <button
          key={`minute-${m}`}
          className={`combined-time-unit ${isSelected ? 'selected' : ''}`}
          onClick={() => !isPastMinute && handleTimeChange(currentHour, m)}
          disabled={isPastMinute}
          style={{
            backgroundColor: isSelected ? themeColor : 'transparent',
            color: isSelected ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
            opacity: isPastMinute ? 0.5 : 1,
            cursor: isPastMinute ? 'not-allowed' : 'pointer',
          }}
        >
          {m.toString().padStart(2, '0')}
        </button>
      );
    }

    return { hours, minutes };
  };

  const displayValue = selectedDate ? 
    selectedDate.format(`DD/MM/YYYY ${ampm ? 'hh:mm A' : 'HH:mm'}`) : 
    '';

  const { hours, minutes } = renderTimeSelection();

  return (
    <div className="combined-datetime-picker-container" ref={containerRef}>
      <div className={`combined-input-wrapper ${isDarkMode ? 'dark' : 'light'} ${disabled ? 'disabled' : ''}`} style={{ '--theme-color': themeColor }}>
        <label className={`combined-label ${selectedDate || placeholder ? 'shrink' : ''}`} htmlFor={name}>
          {label}
        </label>
        <input
          id={name}
          type="text"
          className="combined-input"
          value={displayValue}
          placeholder={placeholder}
          readOnly
          onClick={toggleDateTimePicker}
          disabled={disabled}
          name={name}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
          aria-label={label}
        />
        <div className="combined-input-adornments">
          {selectedDate && (
            <button
              type="button"
              className="combined-clear-button"
              onClick={handleClear}
              aria-label="Clear date and time"
            >
              <i className="fa fa-times"></i>
            </button>
          )}
          <button
            type="button"
            className="combined-calendar-button"
            onClick={toggleDateTimePicker}
            disabled={disabled}
            aria-label="Open date and time picker"
          >
            <i className="fa fa-calendar"></i>
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="combined-datetime-picker-overlay">
          <div className={`combined-datetime-picker-popup ${isDarkMode ? 'dark' : 'light'}`} style={{ '--theme-color': themeColor }}>
            {/* Calendar Section */}
            <div className="combined-calendar-section">
              <div className="combined-calendar-header">
                <button type="button" onClick={() => navigateMonth(-1)} className="combined-nav-button">
                  <i className="fa fa-chevron-left"></i>
                </button>
                <span className="combined-month-year">
                  {currentMonth.format('MMMM YYYY')}
                </span>
                <button type="button" onClick={() => navigateMonth(1)} className="combined-nav-button">
                  <i className="fa fa-chevron-right"></i>
                </button>
              </div>
              
              <div className="combined-calendar-weekdays">
                {moment.weekdaysMin().map(day => (
                  <div key={day} className="combined-weekday">{day}</div>
                ))}
              </div>
              
              <div className="combined-calendar-grid">
                {renderCalendar()}
              </div>
            </div>

            {/* Time Section */}
            <div className="combined-time-section">
              <div className="combined-time-header">
                <span>Select Time</span>
              </div>
              
              <div className="combined-time-selection">
                <div className="combined-time-group">
                  <label>Hours</label>
                  <div className="combined-time-list">
                    {hours}
                  </div>
                </div>
                
                <div className="combined-time-group">
                  <label>Minutes</label>
                  <div className="combined-time-list">
                    {minutes}
                  </div>
                </div>
                
                {ampm && (
                  <div className="combined-time-group">
                    <label>Period</label>
                    <div className="combined-ampm-selection">
                      <button
                        className={`combined-time-unit ${!isPM ? 'selected' : ''}`}
                        onClick={() => {
                          const now = moment();
                          const isToday = selectedDate && selectedDate.isSame(now, 'day');
                          const wouldBePastTime = isToday && now.hour() >= 12; // If it's PM now, AM would be past
                          
                          if (!wouldBePastTime) {
                            setIsPM(false);
                            const newHour = currentHour >= 12 ? currentHour - 12 : currentHour;
                            handleTimeChange(newHour === 0 ? 0 : newHour, currentMinute);
                          }
                        }}
                        disabled={(() => {
                          const now = moment();
                          const isToday = selectedDate && selectedDate.isSame(now, 'day');
                          return isToday && now.hour() >= 12; // Disable AM if it's already PM today
                        })()}
                        style={{
                          backgroundColor: !isPM ? themeColor : 'transparent',
                          color: !isPM ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
                          opacity: (() => {
                            const now = moment();
                            const isToday = selectedDate && selectedDate.isSame(now, 'day');
                            return isToday && now.hour() >= 12 ? 0.5 : 1;
                          })(),
                          cursor: (() => {
                            const now = moment();
                            const isToday = selectedDate && selectedDate.isSame(now, 'day');
                            return isToday && now.hour() >= 12 ? 'not-allowed' : 'pointer';
                          })(),
                        }}
                      >
                        AM
                      </button>
                      <button
                        className={`combined-time-unit ${isPM ? 'selected' : ''}`}
                        onClick={() => {
                          setIsPM(true);
                          const newHour = currentHour < 12 ? currentHour + 12 : currentHour;
                          handleTimeChange(newHour, currentMinute);
                        }}
                        style={{
                          backgroundColor: isPM ? themeColor : 'transparent',
                          color: isPM ? '#ffffff' : (isDarkMode ? '#ffffff' : '#000000'),
                        }}
                      >
                        PM
                      </button>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="combined-datetime-actions">
                <button type="button" className="combined-action-button cancel" onClick={toggleDateTimePicker}>
                  Cancel
                </button>
                <button type="button" className="combined-action-button confirm" onClick={toggleDateTimePicker}
                  style={{ backgroundColor: themeColor, borderColor: themeColor }}>
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CombinedDateTimePicker;
